/* eslint-disable id-length */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FormDialog from '../../../Components/Modal/modal';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import UseAxios from '../../../Api/http/http';
import { UploadResumeApi } from '../../../Api/request/resourceRequest';
import { CANCEL_TEXT, STAFF_AUGMENTATION } from '../../../Constants/strings';
import 'dayjs/locale/en';
import { Typography, Box } from '@mui/material';
import './uploadResumePopUp.scss';

// eslint-disable-next-line max-lines-per-function
const UploadResumeDialog = ({ openAddModal, handleClose, selectedUploadRow, lastUpdatedBy, setRows }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const { snackbarOpen } = useSnackbar();

  const resetValues = () => {
    setSelectedFile(null);
    setFileError('');
  };

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const validateFileType = (fileName) => {
    const validExtensions = ['pdf'];
    return validExtensions.includes(fileName.split('.').pop()
      .toLowerCase());
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return setFileError('Please select a file to upload.');

    if (!validateFileType(selectedFile.name)) {
      return setFileError('Allowed file type: PDF.');
    }

    const MAX_FILE_SIZE_MB = 10;
    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return setFileError(`File size should not exceed ${MAX_FILE_SIZE_MB} MB.`);
    }

    const formData = new FormData();
    formData.append('staffingId', selectedUploadRow.staffingId);
    formData.append('employeeId', selectedUploadRow.employeeId);
    formData.append('resumeTrackerId', selectedUploadRow?.resumeTrackerId || '');
    formData.append('others', selectedUploadRow?.others || '');
    formData.append('lastUpdatedBy', lastUpdatedBy);
    formData.append('resumeFile', selectedFile);

    try {
      const response = await UseAxios(
        UploadResumeApi(formData),
      );

      if (response.status === 201) {
        snackbarOpen('Resume uploaded Successfully', 'success');
        setRows((prevRows) =>
          prevRows.map((row) => {
            const isEmployeeMatch
              = row.staffingId === selectedUploadRow.staffingId
              && row.employeeId === selectedUploadRow.employeeId
              && row.employeeId !== 'N/A';

            const isConsultantMatch
              = row.staffingId === selectedUploadRow.staffingId
              && row.others === selectedUploadRow.others
              && (!row.employeeId || row.employeeId === 'N/A');

            if (isEmployeeMatch || isConsultantMatch) {
              return {
                ...row,
                isResumeUploaded: true,
                showConvert: true,
                resumeTrackerId: response?.data?.resumeId,
              };
            }
            return row;
          }),
        );
      } else {
        const errorMessage = response.data?.response?.data?.error || response.data?.response?.data?.message;
        snackbarOpen(errorMessage, 'error');
      }

      handleCloseWithReset();
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      snackbarOpen(errorMessage, 'error');
      handleCloseWithReset();
    } finally {
      resetValues();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileError('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    handleFileChange({ target: { files: [file] } });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <FormDialog
      title={STAFF_AUGMENTATION.RESUME_UPLOAD_TITLE}
      open={openAddModal}
      button1Action={handleCloseWithReset}
      dialogPaperCss="upload-resume-dialog-paper"
      dialogCss="upload-resume-dialog"
      dialogBodycontent={
        <div className="upload-resume-dialog-body">
          <Typography variant="caption" className="file-validation">Select pdf to upload*</Typography>
          <Box
            className="leave-upload-box"
            sx={{ display: 'flex', gap: '1rem', alignItems: 'center', cursor: 'pointer', marginTop: '1%' }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <label htmlFor="file-upload" className="file-upload-button">Choose file</label>
            <input id="file-upload" type="file" className="file-input" accept=".pdf" onChange={handleFileChange} style={{ display: 'none' }} />
            <span className="file-name">{selectedFile ? selectedFile.name : 'No file chosen'}</span>
          </Box>
          <Typography variant="caption" className="maximum-size">Maximum size to upload: 10MB</Typography>
          {fileError && <Typography className="error-message" color="error">{fileError}</Typography>}
        </div>
      }
      button1={CANCEL_TEXT}
      button2={STAFF_AUGMENTATION.UPLOAD}
      button2Action={handleFileUpload}
      cancelButtonId="manual-leave-upload-cancel-btn"
      submitButtonId="manual-leave-upload-submit-btn"
      button2Disabled= {!selectedFile || fileError}
    />
  );
};

UploadResumeDialog.propTypes = {
  openAddModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedUploadRow: PropTypes.object,
  lastUpdatedBy: PropTypes.string.isRequired,
  setRows: PropTypes.func.isRequired,
};

export default UploadResumeDialog;
